import React, { useState } from 'react';
import styled from 'styled-components';

import PanchangamHeader from '../PanchangamHeader';
import SectionBlock from './SectionBlock';
import PanchaAngaSection from './PanchaAngaSection';
import SunMoonTimesSection from './SunMoonTimesSection';
import InauspiciousPeriodsSection from './InauspiciousPeriodsSection';
import AuspiciousPeriodsSection from './AuspiciousPeriodsSection';
import GowriPanchangamSection from './GowriPanchangamSection';
import HoraSection from './HoraSection';
import FestivalsSection from './FestivalsSection';
import HoraDrillDown from './HoraDrillDown';
import FestivalDrillDown from './FestivalDrillDown';
import LiveStatusFooter from './LiveStatusFooter';

/*
 * PanchangamSheet — top-level orchestrator.
 *
 * CSS Grid layout (desktop ≥900px):
 *   Row 1: PanchangamHeader (full width)
 *   Row 2: PanchaAnga | SunMoonTimes
 *   Row 3: Inauspicious | Auspicious
 *   Row 4: GowriPanchangam (full width)
 *   Row 5: Hora | Festivals
 *   Sticky: LiveStatusFooter
 *
 * Below 900px: single column; same section order.
 * Page max-width: 1100px (per UX §6).
 */

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding-bottom: 80px; /* room for sticky footer */
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${p => p.theme.spacing[3]}px;
  margin-top: ${p => p.theme.spacing[4]}px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media print {
    grid-template-columns: 1fr;
    gap: ${p => p.theme.spacing[2]}px;
  }
`;

const PanchangamSheet = ({ dailyPanchang, now, lang = 'en', siddhanta }) => {
  const [horaModalOpen, setHoraModalOpen] = useState(false);
  const [festivalModalOpen, setFestivalModalOpen] = useState(false);

  if (!dailyPanchang) return null;

  const horaChoghadiya = dailyPanchang.hora_choghadiya || dailyPanchang.horaChoghadiya;
  const allHoras = horaChoghadiya?.horas || [];
  const allFestivals = dailyPanchang.festivals?.upcoming || dailyPanchang.festivals?.festivals || [];

  return (
    <Container>
      <PanchangamHeader
        header={dailyPanchang.header}
        festivals={dailyPanchang.festivals}
        lang={lang}
        siddhanta={siddhanta}
      />

      <Grid>
        <SectionBlock titleTa="பஞ்சாங்கம்" titleEn="Pancha Anga">
          <PanchaAngaSection
            fiveLimbs={dailyPanchang.five_limbs}
            now={now}
            lang={lang}
          />
        </SectionBlock>

        <SectionBlock titleTa="சூரிய சந்திர நேரம்" titleEn="Sun/Moon Times">
          <SunMoonTimesSection
            sunMoon={dailyPanchang.sun_moon}
            lang={lang}
          />
        </SectionBlock>

        <SectionBlock titleTa="அசுப காலம்" titleEn="Inauspicious" tone="red">
          <InauspiciousPeriodsSection
            inauspicious={dailyPanchang.inauspicious}
            now={now}
            lang={lang}
          />
        </SectionBlock>

        <SectionBlock titleTa="சுப காலம்" titleEn="Auspicious" tone="green">
          <AuspiciousPeriodsSection
            auspicious={dailyPanchang.auspicious}
            now={now}
            lang={lang}
          />
        </SectionBlock>

        <SectionBlock titleTa="கௌரி பஞ்சாங்கம்" titleEn="Gowri Panchangam" fullWidth>
          <GowriPanchangamSection
            gowri={dailyPanchang.gowri}
            now={now}
            lang={lang}
          />
        </SectionBlock>

        <SectionBlock titleTa="ஹோரை" titleEn="Hora">
          <HoraSection
            horaChoghadiya={horaChoghadiya}
            now={now}
            lang={lang}
            onShowAllHoras={() => setHoraModalOpen(true)}
          />
        </SectionBlock>

        <SectionBlock titleTa="பண்டிகைகள்" titleEn="Festivals">
          <FestivalsSection
            festivals={dailyPanchang.festivals}
            lang={lang}
            onShowFullMonth={() => setFestivalModalOpen(true)}
          />
        </SectionBlock>
      </Grid>

      <HoraDrillDown
        open={horaModalOpen}
        onClose={() => setHoraModalOpen(false)}
        horas={allHoras}
        lang={lang}
      />

      <FestivalDrillDown
        open={festivalModalOpen}
        onClose={() => setFestivalModalOpen(false)}
        festivals={allFestivals}
        lang={lang}
      />

      <LiveStatusFooter
        dailyPanchang={dailyPanchang}
        now={now}
        lang={lang}
      />
    </Container>
  );
};

export default PanchangamSheet;
